import React, { useRef, useState } from "react";
import { useParams } from "react-router";
import { CaseFile } from "../models/CaseFile";
import {UserRole} from "../models/User";
import ActionConfirmationModal, {ACTION_CONFIGS} from "../components/common/ActionConfirmationModal";
import ConfirmationModal from "../components/common/ConfirmationModal";
import { Button } from "@/components/ui/button";
import PageHeader from "../components/common/Page/PageHeader";
import {CaseFilesTable, CaseFilesTableRef} from "../components/CaseFiles/CaseFilesTable";
import {ActiveJobsExplorer} from "../models/Jobs/ActiveJobsExplorer";
import {ExportCaseFilesToCsvStatusTable} from "../components/CaseFiles/ExportCaseFilesToCsvStatusTable";
import { Settings2, X } from "lucide-react";
import { useManagementDrawer } from "../hooks/useManagementDrawer";
import { useRequiresReason } from "../hooks/useRequiresReason";

interface CaseFilesPageProps {
  userRole: UserRole;
  isAssignedUser: boolean;
}

export const CaseFilesPage: React.FC<CaseFilesPageProps> = ({
  userRole,
  isAssignedUser
}: CaseFilesPageProps) => {
  const requiresReason = useRequiresReason();
  const { organizationId } = useParams();
  const [isConfirmExportModalOpen, setIsConfirmExportModalOpen] = React.useState<boolean>(false);
  const [newCaseFilesForDeletion, setNewCaseFilesForDeletion] = useState<CaseFile[]>([]);
  const [drawerOpen, setDrawerOpen] = useManagementDrawer(0);
  const caseFilesTableRef = useRef<CaseFilesTableRef>(null);

  const startCurrentViewExportToCsv = (reason?: string) =>
    caseFilesTableRef.current?.exportCurrentViewToCsv(reason);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <PageHeader
        title="Case Files"
        action={userRole !== UserRole.Viewer && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className="gap-2"
          >
            <Settings2 className="size-4" />
            Manage exports
          </Button>
        )}
      />
      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
        <div className="flex flex-col min-h-0 flex-1 min-w-0">
          <CaseFilesTable
            ref={caseFilesTableRef}
            organizationId={organizationId ?? ""}
            activeJobsExplorer={ActiveJobsExplorer.Export}
            newCaseFilesForDeletion={newCaseFilesForDeletion}
            setNewCasesFilesForDeletion={setNewCaseFilesForDeletion}
            disabled={userRole === UserRole.Viewer}
          />
        </div>
        {userRole !== UserRole.Viewer && drawerOpen && (
          <div className="flex flex-col min-h-0 w-full lg:w-96 shrink-0 rounded-md border bg-background shadow-sm">
            <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
              <h3 className="font-semibold text-foreground">Manage exports</h3>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close"
              >
                <X className="size-4" />
              </Button>
            </div>
            <div className="flex flex-col flex-1 min-h-0 gap-4 p-4 overflow-auto">
              {requiresReason ? (
                <ActionConfirmationModal
                  actionConfig={ACTION_CONFIGS.EXPORT_CASE_FILES_VIEW}
                  onConfirm={async (reason) => {
                    startCurrentViewExportToCsv(reason);
                  }}
                >
                  <Button>Export current files view</Button>
                </ActionConfirmationModal>
              ) : (
                <Button onClick={() => setIsConfirmExportModalOpen(true)}>
                  Export current files view
                </Button>
              )}
              <ExportCaseFilesToCsvStatusTable
                userRole={userRole}
                isAssignedUser={isAssignedUser}
                organizationId={organizationId ?? ""}
              />
            </div>
          </div>
        )}
      </div>
      {isConfirmExportModalOpen && (
        <ConfirmationModal
          open={isConfirmExportModalOpen}
          onClose={() => setIsConfirmExportModalOpen(false)}
          onConfirm={async () => {
            startCurrentViewExportToCsv();
            setIsConfirmExportModalOpen(false);
          }}
          message="Are you sure you want to export case files from the current view?"
          statusColor="primary"
          buttonText="Confirm"
        />
      )}
    </div>
  );
};
